/**
 * CamPlatformIcons.jsx
 * Custom SVG icon system for StarTip's webcam platform integrations.
 *
 * Two variants per platform:
 *  - Badge  (self-contained, own background) → ModelProfile accordion
 *  - Icon   (white strokes, no background)   → TipPage chips, HomePage filters
 *
 * Colors extracted from reference logos — compositions are original (not copies).
 */

// ─── AMATEUR.TV ───────────────────────────────────────────────────────────────
// Reference: dark/black bg, 3D golden star, white "amateur" text
// Our version: flat geometric star on dark, "atv" label — same palette, different style

export const AmateurTvBadge = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 40 40" className={className}>
    <rect width="40" height="40" rx="8" fill="#111111" />
    {/* Flat geometric 5-point star in gold — different from 3D metallic original */}
    <polygon
      points="20,7 22.94,15.91 32.36,15.91 25.21,21.09 27.83,30 20,24.91 12.17,30 14.79,21.09 7.64,15.91 17.06,15.91"
      fill="#FFD700"
    />
    <text
      x="20" y="38"
      fontSize="7" fontWeight="bold" fontFamily="Arial,sans-serif"
      fill="white" textAnchor="middle" letterSpacing="0.3"
    >atv</text>
  </svg>
);

export const AmateurTvIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-label="amateur.tv">
    <polygon
      points="12,3 13.8,8.5 19.4,8.5 15,11.8 16.7,17.3 12,14.1 7.3,17.3 9,11.8 4.6,8.5 10.2,8.5"
      fill="#FFD700"
    />
    <text x="12" y="23" fontSize="4.5" fontWeight="bold" fontFamily="Arial,sans-serif" fill="white" textAnchor="middle">atv</text>
  </svg>
);

// ─── CHATURBATE ───────────────────────────────────────────────────────────────
// Reference: white bg, ornate script "C" in orange-to-amber gradient (#fa7d1b→#ffda5c), dark teal outline (#1f576b)
// Our version: white bg, bold geometric thick-arc C in same gradient + teal inner shape

export const ChaturbateBadge = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 40 40" className={className}>
    <defs>
      <linearGradient id="cb-badge-g" x1="0.3" y1="0" x2="0.3" y2="1">
        <stop offset="0%" stopColor="#FA7D1B" />
        <stop offset="100%" stopColor="#FFDA5C" />
      </linearGradient>
    </defs>
    <rect width="40" height="40" rx="8" fill="white" />
    {/* Outer C arc — dark teal (like the outline in original) */}
    <path
      d="M31 11 A14 14 0 1 0 31 29"
      stroke="#1F576B"
      strokeWidth="10"
      fill="none"
      strokeLinecap="round"
    />
    {/* Inner C arc — orange-to-amber gradient */}
    <path
      d="M31 11 A14 14 0 1 0 31 29"
      stroke="url(#cb-badge-g)"
      strokeWidth="7"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

export const ChaturbateIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-label="Chaturbate">
    <path
      d="M19 7 A10 10 0 1 0 19 17"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

// ─── OLECAMS ──────────────────────────────────────────────────────────────────
// Reference: yellow-to-red vertical gradient rounded square (#fcc000→#f57d22→#CC0000), white text
// Our version: same warm gradient bg, white abstract OC monogram + accent lines

export const OlecamsBadge = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 40 40" className={className}>
    <defs>
      <linearGradient id="ole-badge-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FCC000" />
        <stop offset="45%" stopColor="#F57D22" />
        <stop offset="100%" stopColor="#CC0000" />
      </linearGradient>
    </defs>
    <rect width="40" height="40" rx="8" fill="url(#ole-badge-g)" />
    {/* Abstract camera aperture — circular lines forming a "lens" */}
    <circle cx="20" cy="18" r="9" stroke="white" strokeWidth="2" fill="none" />
    <circle cx="20" cy="18" r="4.5" stroke="white" strokeWidth="2" fill="none" />
    <circle cx="20" cy="18" r="1.5" fill="white" />
    {/* "OC" letter monogram below */}
    <text
      x="20" y="35"
      fontSize="8" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif"
      fill="white" textAnchor="middle" letterSpacing="1"
    >OC</text>
  </svg>
);

export const OlecamsIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-label="Olecams">
    <circle cx="12" cy="11" r="6" stroke="white" strokeWidth="2" />
    <circle cx="12" cy="11" r="3" stroke="white" strokeWidth="2" />
    <circle cx="12" cy="11" r="1" fill="white" />
    <text x="12" y="22.5" fontSize="4.5" fontWeight="900" fontFamily="Arial,sans-serif" fill="white" textAnchor="middle">OC</text>
  </svg>
);

// ─── CAM4 ─────────────────────────────────────────────────────────────────────
// Reference: black bg (#000000), white bold "CAM", orange "4" (#FF5500)
// Our version: same exact color scheme, different proportion/layout to distinguish

export const Cam4Badge = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 40 40" className={className}>
    <rect width="40" height="40" rx="8" fill="#111111" />
    {/* Horizontal divider line */}
    <line x1="4" y1="21" x2="36" y2="21" stroke="#FF5500" strokeWidth="0.8" strokeOpacity="0.5" />
    <text
      x="20" y="18"
      fontSize="10.5" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif"
      fill="white" textAnchor="middle" letterSpacing="0.5"
    >CAM</text>
    <text
      x="20" y="32"
      fontSize="13" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif"
      fill="#FF5500" textAnchor="middle"
    >4</text>
  </svg>
);

export const Cam4Icon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-label="CAM4">
    <text x="12" y="11" fontSize="6.5" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif" fill="white" textAnchor="middle">CAM</text>
    <text x="12" y="20" fontSize="8" fontWeight="900" fontFamily="Arial Black,Arial,sans-serif" fill="#FF5500" textAnchor="middle">4</text>
  </svg>
);

// ─── STRIPCHAT ────────────────────────────────────────────────────────────────
// Reference: deep crimson red bg (#B52035), white speech-bubble with martini
// glass cutout — the cocktail glass (bowl + stem + base) is transparent/red,
// creating a negative-space logo inside the bubble.

export const StripchatBadge = ({ className = "w-9 h-9" }) => (
  <svg viewBox="0 0 40 40" className={className}>
    {/* Red background */}
    <rect width="40" height="40" rx="8" fill="#B52035" />
    {/* White speech bubble — oval with bottom-left tail */}
    <path
      d="M9 7 Q9 4 12 4 L28 4 Q31 4 31 7 L31 22 Q31 25 28 25 L21 25 L16.5 31 L15 25 L12 25 Q9 25 9 22 Z"
      fill="white"
    />
    {/* Martini glass cutout (red = same as background) */}
    {/* Bowl: wide triangle */}
    <polygon points="13,8 27,8 20,17.5" fill="#B52035" />
    {/* Stem */}
    <rect x="19.2" y="17" width="1.6" height="5" fill="#B52035" />
    {/* Base */}
    <rect x="15.5" y="21.8" width="9" height="1.8" rx="0.6" fill="#B52035" />
  </svg>
);

export const StripchatIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-label="Stripchat">
    {/* White speech bubble */}
    <path
      d="M4 3 Q4 1.5 5.5 1.5 L18.5 1.5 Q20 1.5 20 3 L20 13.5 Q20 15 18.5 15 L13.5 15 L10.5 18.5 L9.5 15 L5.5 15 Q4 15 4 13.5 Z"
      fill="white"
    />
    {/* Martini glass in bg color — use currentColor so it adapts */}
    <polygon points="7.5,4 16.5,4 12,10.5" fill="#B52035" />
    <rect x="11.4" y="10" width="1.2" height="3.2" fill="#B52035" />
    <rect x="9.2" y="13" width="5.6" height="1.2" rx="0.4" fill="#B52035" />
  </svg>
);

/**
 * Convenience map — keys match backend _CAM_PLATFORMS values.
 */
export const CAM_PLATFORM_ICONS = {
  amateur_tv:  AmateurTvIcon,
  chaturbate:  ChaturbateIcon,
  cam4:        Cam4Icon,
  olecams:     OlecamsIcon,
  stripchat:   StripchatIcon,
};

export const CAM_PLATFORM_BADGES = {
  amateur_tv:  AmateurTvBadge,
  chaturbate:  ChaturbateBadge,
  cam4:        Cam4Badge,
  olecams:     OlecamsBadge,
  stripchat:   StripchatBadge,
};

